import React, { useEffect, useState } from 'react';
import { Card, Col, Form, Placeholder, Row } from 'react-bootstrap';
import { ActiveUsersGroup, IActiveUsers } from '../dto/iStatistic';
import { StatisticsService } from '../StatisticsService';
import { ErrorToast } from '../../../utils/toasters';
import { IActiveUsersSelection, StatisticsActiveUsersModal } from './StatisticsActiveUsersModal';

function StatValue({ value, isLoading, fontSize }: { value?: number; isLoading: boolean; fontSize: number }) {
    return (
        <div className="d-flex align-items-center" style={{ height: fontSize * 1.1 }}>
            {isLoading ? (
                <Placeholder as="span" animation="glow" className="w-100">
                    <Placeholder className="skeleton skeleton-stat" style={{ width: fontSize * 1.6 }} />
                </Placeholder>
            ) : (
                <span style={{ fontSize, fontWeight: 700, lineHeight: 1.1 }}>{value ?? '-'}</span>
            )}
        </div>
    );
}

export function StatisticsActiveUsers() {
    const [minutes, setMinutes] = useState(5);
    const [minutesInput, setMinutesInput] = useState(5);
    const [data, setData] = useState<IActiveUsers>();
    const [selection, setSelection] = useState<IActiveUsersSelection>();
    const [isOnlineLoading, setIsOnlineLoading] = useState(true);
    const isFirstLoad = isOnlineLoading && !data;

    useEffect(() => {
        const timeout = setTimeout(() => setMinutes(minutesInput), 400);
        return () => clearTimeout(timeout);
    }, [minutesInput]);

    useEffect(() => {
        let isCurrent = true;

        const load = () =>
            StatisticsService.getActiveUsers(minutes)
                .then(res => {
                    if (isCurrent) setData(res);
                })
                .catch(err => {
                    if (isCurrent) ErrorToast(err);
                });

        setIsOnlineLoading(true);
        load().finally(() => {
            if (isCurrent) setIsOnlineLoading(false);
        });

        const timer = setInterval(load, 30000);

        return () => {
            isCurrent = false;
            clearInterval(timer);
        };
    }, [minutes]);

    const cards = [
        {
            key: 'dau',
            minutes: 1440,
            title: 'DAU',
            subtitle: 'Daily active users',
            value: data?.dau,
            color: '#3699ff',
            text: 'Unique users who used the app at least once in the last 24 hours.',
        },
        {
            key: 'wau',
            minutes: 10080,
            title: 'WAU',
            subtitle: 'Weekly active users',
            value: data?.wau,
            color: '#8950fc',
            text: 'Unique users who used the app at least once in the last 7 days.',
        },
        {
            key: 'mau',
            minutes: 43200,
            title: 'MAU',
            subtitle: 'Monthly active users',
            value: data?.mau,
            color: '#f64e60',
            text: 'Unique users who used the app at least once in the last 30 days.',
        },
        {
            key: 'yau',
            minutes: 525600,
            title: 'YAU',
            subtitle: 'Yearly active users',
            value: data?.yau,
            color: '#ffa800',
            text: 'Unique users who used the app at least once in the last 365 days.',
        },
    ];

    const onlineNowCards = [
        {
            key: 'brand',
            group: 'brand' as ActiveUsersGroup,
            title: 'Brand',
            subtitle: 'brand and brand master',
            value: data?.activeNowBrand,
        },
        {
            key: 'agency',
            group: 'agency' as ActiveUsersGroup,
            title: 'Agency',
            subtitle: 'agency and agency master',
            value: data?.activeNowAgency,
        },
        {
            key: 'influencer',
            group: 'influencer' as ActiveUsersGroup,
            title: 'Influencer',
            subtitle: 'influencers',
            value: data?.activeNowInfluencer,
        },
        {
            key: 'internal',
            group: 'internal' as ActiveUsersGroup,
            title: 'Internal',
            subtitle: 'internal users',
            value: data?.activeNowInternal,
        },
    ];

    return (
        <div className="mt-3 mb-5">
            <div className="mb-3">
                <h4 className="mb-0">Online now</h4>
                <div className="text-muted">Users active right now, in total and by user type.</div>
            </div>

            <Card
                className="mb-6 p-6"
                style={{ borderLeft: '6px solid #1bc5bd' }}
                role="button"
                onClick={() => setSelection({ title: 'Online now', minutes })}
            >
                <Row className="align-items-center">
                    <Col md={5} className="mb-4 mb-md-0">
                        <div className="d-flex align-items-center">
                            <span
                                style={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    background: '#1bc5bd',
                                    display: 'inline-block',
                                    marginRight: 12,
                                }}
                            />
                            <span className="fs-4 fw-bold">Online now</span>
                        </div>
                        <StatValue value={data?.activeNow} isLoading={isOnlineLoading} fontSize={56} />
                        <div className="text-muted">users active in the last {minutes} min</div>
                    </Col>
                    <Col md={7} onClick={e => e.stopPropagation()}>
                        <Form.Group style={{ maxWidth: 260 }}>
                            <Form.Label className="fw-bold">Active window (minutes)</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={minutesInput}
                                onChange={e => setMinutesInput(Math.max(1, Number(e.target.value) || 5))}
                            />
                            <Form.Text className="text-muted">
                                A user counts as "online" if they made a request within this many minutes. Refreshes
                                every 30 seconds.
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            </Card>

            <div className="mb-3">
                <h4 className="mb-0">Online now by user type</h4>
                <div className="text-muted">Same active window, split by user type.</div>
            </div>

            <Row>
                {onlineNowCards.map(c => (
                    <Col md={3} key={c.key} className="mb-4">
                        <Card
                            className="p-6 h-100"
                            style={{ borderTop: '4px solid #1bc5bd' }}
                            role="button"
                            onClick={() => setSelection({ title: `Online now - ${c.title}`, minutes, group: c.group })}
                        >
                            <div className="fw-bold" style={{ color: '#1bc5bd' }}>
                                Online now - {c.title}
                            </div>
                            <div className="text-muted mb-2">{c.subtitle}</div>
                            <StatValue value={c.value} isLoading={isOnlineLoading} fontSize={40} />
                            <div className="text-muted mt-3">Users active in the last {minutes} min.</div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <hr className="my-8" />

            <div className="mb-3">
                <h4 className="mb-0">Active users over time</h4>
                <div className="text-muted">Unique users per rolling period.</div>
            </div>

            <Row>
                {cards.map(c => (
                    <Col md={3} key={c.key} className="mb-4">
                        <Card
                            className="p-6 h-100"
                            style={{ borderTop: `4px solid ${c.color}` }}
                            role="button"
                            onClick={() => setSelection({ title: `${c.title} - ${c.subtitle}`, minutes: c.minutes })}
                        >
                            <div className="fw-bold" style={{ color: c.color }}>
                                {c.title}
                            </div>
                            <div className="text-muted mb-2">{c.subtitle}</div>
                            <StatValue value={c.value} isLoading={isFirstLoad} fontSize={40} />
                            <div className="text-muted mt-3">{c.text}</div>
                        </Card>
                    </Col>
                ))}
            </Row>

            <Card className="p-6 bg-light mt-8">
                <div className="fw-bold mb-2">How to read these numbers</div>
                <div className="text-muted">
                    Each person is counted once per period, no matter how many times they open the app. DAU, WAU, MAU
                    and YAU are rolling windows (the last 24 hours, 7 days, 30 days and 365 days), not calendar days, so
                    DAU ≤ WAU ≤ MAU ≤ YAU. The ratio DAU / MAU shows how "sticky" the app is: the closer to 100%, the
                    more of your monthly users come back every day.
                </div>
            </Card>
            <StatisticsActiveUsersModal selection={selection} onClose={() => setSelection(undefined)} />
        </div>
    );
}
